import React from 'react';
import type { Room } from '@deadpan/shared';
import type { Socket } from 'socket.io-client';
import type { ServerToClientEvents, ClientToServerEvents } from '@deadpan/shared';
import { MIN_PLAYERS_FOR_WORDSMITH, getLanguage } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';

interface DeadpanConfigProps {
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
  currentRoom: Room;
  explanationTime: number;
  prepTime: number;
  timerError: string | null;
  setExplanationTime: (val: number) => void;
  setPrepTime: (val: number) => void;
  createTimerInputHandler: (type: 'explanation' | 'prep', setter: (val: number) => void, currentValue: number) => {
    onFocus: () => void;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
  };
}

export const DeadpanConfig: React.FC<DeadpanConfigProps> = ({
  socket,
  currentRoom,
  explanationTime,
  prepTime,
  timerError,
  setExplanationTime,
  setPrepTime,
  createTimerInputHandler,
}) => {
  const t = useTranslations();
  const isZh = getLanguage() === 'zh';

  return (
    <>
      {/* Wordsmith Assignment */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-pink-50 border-2 border-pink-200 rounded-xl">
        <h3 className="text-base sm:text-lg font-bold text-pink-800 mb-3 sm:mb-4 flex items-center gap-2">
          <span>✍️</span>
          {t.games.deadpan.assignWordsmith}
        </h3>
        {currentRoom.players.length < MIN_PLAYERS_FOR_WORDSMITH && (
          <p className="text-sm text-pink-700 mb-4">{t.games.deadpan.wordsmithRequired}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {currentRoom.players.map((player) => {
            const canAssign = currentRoom.players.length >= MIN_PLAYERS_FOR_WORDSMITH;
            const isAssigned = currentRoom.wordsmithId === player.id;
            return (
              <button
                key={player.id}
                onClick={() => {
                  if (socket && canAssign) {
                    socket.emit('client:assign-wordsmith', player.id);
                  }
                }}
                disabled={!canAssign}
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  isAssigned
                    ? 'border-pink-500 bg-pink-100 shadow-md'
                    : canAssign
                    ? 'border-pink-200 bg-white hover:border-pink-300 hover:shadow-sm'
                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800">{player.name}</span>
                  {isAssigned && (
                    <span className="text-pink-600 font-bold">✓</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {currentRoom.wordsmithId && currentRoom.players.length >= MIN_PLAYERS_FOR_WORDSMITH && (
          <button
            onClick={() => {
              if (socket && currentRoom.wordsmithId) {
                socket.emit('client:assign-wordsmith', currentRoom.wordsmithId);
              }
            }}
            className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
          >
            {t.games.deadpan.removeWordsmith}
          </button>
        )}
      </div>

      {/* Timer Settings */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl">
        <h3 className="text-base sm:text-lg font-bold text-indigo-800 mb-3 sm:mb-4 flex items-center gap-2">
          <span>⏱️</span>
          {t.ui.timerSettings}
        </h3>
        {timerError && (
          <div className="mb-4 p-3 bg-red-100 border-2 border-red-300 rounded-lg">
            <p className="text-sm font-semibold text-red-800">{timerError}</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.ui.explanationDisplayTimeLabel}
            </label>
            <input
              type="number"
              min="5"
              max="120"
              value={explanationTime || ''}
              {...createTimerInputHandler('explanation', setExplanationTime, explanationTime)}
              className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                timerError && (explanationTime < 5 || explanationTime > 120)
                  ? 'border-red-500 focus:border-red-600'
                  : 'border-indigo-300 focus:border-indigo-500'
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">{t.ui.timerRangeHint || 'Range: 5-120 seconds'}</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.ui.prepTimeLabel}
            </label>
            <input
              type="number"
              min="5"
              max="120"
              value={prepTime || ''}
              {...createTimerInputHandler('prep', setPrepTime, prepTime)}
              className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                timerError && (prepTime < 5 || prepTime > 120)
                  ? 'border-red-500 focus:border-red-600'
                  : 'border-indigo-300 focus:border-indigo-500'
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">{t.ui.timerRangeHint || 'Range: 5-120 seconds'}</p>
          </div>
        </div>
      </div>

      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-violet-50 border-2 border-violet-200 rounded-xl">
        <h3 className="text-base sm:text-lg font-bold text-violet-800 mb-3 sm:mb-4 flex items-center gap-2">
          <span>🎴</span>
          {isZh ? '道具设置' : 'Item Settings'}
        </h3>
        <label className="flex items-center justify-between gap-4 rounded-lg border border-violet-200 bg-white px-4 py-3">
          <div>
            <p className="font-semibold text-gray-800">{isZh ? '启用一本正经道具' : 'Enable Deadpan items'}</p>
            <p className="text-xs text-gray-500 mt-1">
              {isZh ? '本局是否发放并允许使用神秘道具。' : 'Controls whether players receive and can use mystery items this round.'}
            </p>
          </div>
          <input
            type="checkbox"
            checked={currentRoom.deadpanConfig?.itemsEnabled ?? false}
            onChange={(e) => socket?.emit('client:update-deadpan-items-enabled', e.target.checked)}
            className="h-5 w-5 rounded border-violet-300 text-violet-600 focus:ring-violet-500"
          />
        </label>
      </div>
    </>
  );
};

